import { motion } from 'framer-motion';
import { Shield, TrendingUp, Target, ArrowRight, CheckCircle } from 'lucide-react';

const InsuranceHero = () => {
  return (
    <section id="hero" className="relative min-h-screen bg-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Geometric shapes */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-[#90E0EF]/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-32 left-10 w-48 h-48 bg-[#03045E]/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#CAF0F8]/10 rounded-full blur-3xl"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-12 gap-4 h-full">
            {Array.from({ length: 144 }).map((_, i) => (
              <motion.div
                key={i}
                className="bg-[#03045E] rounded-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.3, 0] }}
                transition={{
                  duration: 3,
                  delay: i * 0.02,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full"
            >
              <Shield className="w-4 h-4 text-[#03045E] mr-2" />
              <span className="text-[#03045E] font-semibold text-sm">Your Strategic Partner in Risk Management</span>
            </motion.div>

            {/* Main Heading */}
            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#03045E] leading-tight"
              >
                Transforming Health Insurance from{' '}
                <span className="relative">
                  <span className="text-[#0077B6]">Reactive</span>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ delay: 1.5, duration: 0.8 }}
                    className="absolute bottom-0 left-0 h-1 bg-[#90E0EF] rounded-full"
                  />
                </span>{' '}
                to{' '}
                <span className="relative">
                  <span className="text-[#0077B6]">Proactive</span>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ delay: 2, duration: 0.8 }}
                    className="absolute bottom-0 left-0 h-1 bg-[#90E0EF] rounded-full"
                  />
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="text-xl text-gray-600 leading-relaxed max-w-2xl"
              >
                HIERSTO helps insurance companies cut costs, detect fraud, and deliver value that keeps customers loyal — powered by advanced analytics and AI.
              </motion.p>
            </div>

            {/* Key Points */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="space-y-3"
            >
              {[
                'Predict risks before they become costly claims',
                'Streamline approvals with AI-powered automation',
                'Uncover hidden savings through advanced analytics'
              ].map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 + (index * 0.2), duration: 0.6 }}
                  className="flex items-center space-x-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#0077B6] flex-shrink-0" />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={() => {
                  const element = document.getElementById('cta');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group bg-[#03045E] text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 hover:bg-[#03045E]/90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>Book Strategic Demo</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              
              <motion.button
                onClick={() => {
                  const element = document.getElementById('solution');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group border-2 border-[#0077B6] text-[#0077B6] px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 hover:bg-[#0077B6] hover:text-white transition-all duration-300"
              >
                <span>Learn More</span>
                <Target className="w-5 h-5 group-hover:rotate-45 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Intro Paragraph */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="bg-[#CAF0F8]/30 border border-[#90E0EF]/50 rounded-2xl p-6"
            >
              <p className="text-[#03045E] leading-relaxed">
                Health insurance doesn't have to be a race to the lowest premium. With HIERSTO, you gain the tools to predict risks, prevent costly claims, streamline approvals, and uncover hidden savings — all while building stronger, lasting relationships with your clients.
              </p>
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              {/* Main card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="bg-white rounded-3xl shadow-2xl p-8 border border-[#90E0EF]/20"
              >
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-[#03045E]">Risk Dashboard</h3>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-[#90E0EF] rounded-full"></div>
                      <div className="w-3 h-3 bg-[#0077B6] rounded-full"></div>
                      <div className="w-3 h-3 bg-[#03045E] rounded-full"></div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.2, duration: 0.6 }}
                      className="bg-[#90E0EF]/10 p-4 rounded-xl"
                    >
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-5 h-5 text-[#0077B6]" />
                        <span className="text-sm text-gray-600">Cost Reduction</span>
                      </div>
                      <div className="text-2xl font-bold text-[#03045E] mt-1">35%</div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.4, duration: 0.6 }}
                      className="bg-[#0077B6]/10 p-4 rounded-xl"
                    >
                      <div className="flex items-center space-x-2">
                        <Shield className="w-5 h-5 text-[#0077B6]" />
                        <span className="text-sm text-gray-600">Fraud Detection</span>
                      </div>
                      <div className="text-2xl font-bold text-[#03045E] mt-1">98.5%</div>
                    </motion.div>
                  </div>

                  {/* Progress bars */}
                  <div className="space-y-4">
                    {[
                      { label: 'Claims Processing', value: 85, color: '#90E0EF' },
                      { label: 'Risk Assessment', value: 92, color: '#0077B6' },
                      { label: 'Customer Satisfaction', value: 88, color: '#03045E' }
                    ].map((item, index) => (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.6 + (index * 0.2), duration: 0.6 }}
                        className="space-y-2"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{item.label}</span>
                          <span className="text-sm font-semibold text-[#03045E]">{item.value}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${item.value}%` }}
                            transition={{ delay: 2 + (index * 0.3), duration: 1 }}
                            className="h-2 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Floating elements */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 2, duration: 0.6 }}
                className="absolute -top-4 -right-4 bg-[#0077B6] text-white p-4 rounded-2xl shadow-lg"
              >
                <Shield className="w-6 h-6" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 2.2, duration: 0.6 }}
                className="absolute -bottom-4 -left-4 bg-[#90E0EF] text-[#03045E] p-4 rounded-2xl shadow-lg"
              >
                <TrendingUp className="w-6 h-6" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InsuranceHero;
